import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, Lock, CheckCircle, AlertCircle, Shield, Clock, Eye, EyeOff, Bell } from 'lucide-react';
import { devLog } from '../utils/devLogger';

const ParentSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setupCode = searchParams.get('code');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Mock data - in production, fetch from API using setupCode
  const [parentData] = useState({
    parentName: 'Sarah Davis',
    teenName: 'Emma Davis',
    teenEmail: 'emma@test.com'
  });

  // Step 1: SMS Verification
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);

  // Step 2: Password
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Step 3: Settings
  const [settings, setSettings] = useState({
    contentFilters: {
      supportAvas: true,
      coachAvas: true,
      friendAvas: true,
      memorialAvas: false,
      peerGroups: true,
      adultMentorSessions: false
    },
    timeLimits: {
      dailyLimit: '2hrs',
      quietHoursStart: '22:00',
      quietHoursEnd: '07:00',
      weekendSame: true,
      weekendLimit: '2hrs'
    },
    privacySettings: {
      viewMoodScores: true,
      viewTopicSummaries: true,
      viewTimeSpent: true,
      viewGroups: true,
      viewFullTranscripts: false
    },
    crisisAlerts: {
      smsCrisisButton: true,
      emailCrisisButton: true,
      moodPatternAlert: true
    },
    consent: false
  });

  const [setupComplete, setSetupComplete] = useState(false);

  useEffect(() => {
    if (!setupCode) {
      setError('Invalid setup link. Please use the link from your email.');
    }
  }, [setupCode]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return `+1 (${match[1]})${match[2] ? ' ' + match[2] : ''}${match[3] ? '-' + match[3] : ''}`;
    }
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleSendSMS = () => {
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setError('');
    setSmsSent(true);

    // Mock SMS (TODO: wire to Twilio)
    devLog(`📱 [mock] SMS verification code 654321 queued for ${phoneNumber}`);
  };

  const handleVerifyPhone = () => {
    if (smsCode !== '654321') {
      setError('Invalid verification code. Please try again.');
      return;
    }

    setError('');
    setStep(2);
  };

  const checkPasswordStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength++;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    checkPasswordStrength(newPassword);
  };

  const handleStep2Continue = () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setStep(3);
  };

  const handleCompleteSetup = () => {
    if (!settings.consent) {
      setError('You must verify that you are the parent/legal guardian to continue');
      return;
    }

    setLoading(true);
    setError('');

    // Simulate API call
    setTimeout(() => {
      // Update teen's user in localStorage
      const users = JSON.parse(localStorage.getItem('hibff_users') || '[]');
      const teenIndex = users.findIndex(u => u.email === parentData.teenEmail);

      if (teenIndex !== -1) {
        users[teenIndex].parentSetupComplete = true;
        users[teenIndex].parentSettings = settings;
        localStorage.setItem('hibff_users', JSON.stringify(users));
      }

      // Send email notification to teen (TODO: wire to Postmark)
      devLog(`📧 [mock] Activation email queued for teen ${parentData.teenEmail}`);

      setSetupComplete(true);
      setLoading(false);
    }, 1500);
  };

  const getStrengthColor = () => {
    if (passwordStrength === 0) return 'bg-gray-300';
    if (passwordStrength === 1) return 'bg-red-500';
    if (passwordStrength === 2) return 'bg-yellow-500';
    if (passwordStrength === 3) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    if (passwordStrength === 0) return 'Too weak';
    if (passwordStrength === 1) return 'Weak';
    if (passwordStrength === 2) return 'Fair';
    if (passwordStrength === 3) return 'Good';
    return 'Strong';
  };

  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <img src="/hibff-logo.png" alt="HiBFF" className="h-36 mx-auto mb-4" />
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle size={48} className="text-green-600" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Setup Complete!
            </h1>

            <p className="text-lg text-gray-700 mb-6">
              {parentData.teenName}&apos;s account is now active. They can log in and start using HiBFF.
            </p>

            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <p className="text-sm text-blue-900 mb-2">
                <strong>We&apos;ve sent an email to {parentData.teenEmail}</strong>
              </p>
              <p className="text-sm text-blue-800">
                They&apos;ll receive a notification that their account is ready to use.
              </p>
            </div>

            <button
              onClick={() => window.alert('Parent Dashboard will be available at parent.hibff.com post-investment')}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all mb-4"
            >
              View Parent Dashboard
            </button>

            <p className="text-xs text-gray-500">
              You can access the parent dashboard anytime at parent.hibff.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-36 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">Parent Setup Portal</h1>
          <p className="text-gray-600 mt-2">
            Hi {parentData.parentName}, thank you for helping set up {parentData.teenName}&apos;s account.
          </p>
        </div>

        {/* Setup Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                1
              </div>
              <span className="text-sm font-medium hidden sm:inline">Verify</span>
            </div>
            <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'} mx-2`}></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                2
              </div>
              <span className="text-sm font-medium hidden sm:inline">Password</span>
            </div>
            <div className={`w-12 h-0.5 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'} mx-2`}></div>
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                3
              </div>
              <span className="text-sm font-medium hidden sm:inline">Settings</span>
            </div>
          </div>

          <p className="text-center text-sm text-gray-600 mb-6">
            Step {step} of 3
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Step 1: SMS Verification */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <Phone size={32} className="text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Verify Your Phone Number</h2>
                <p className="text-sm text-gray-600">
                  We&apos;ll send you a verification code to confirm your identity
                </p>
              </div>

              {!smsSent ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSendSMS}
                    className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
                  >
                    Send Verification Code
                  </button>
                </>
              ) : (
                <>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                    <p className="text-sm text-green-900 font-semibold mb-1">
                      ✅ Code sent to {phoneNumber}
                    </p>
                    <p className="text-xs text-green-800">
                      Check your browser console (F12 → Console) to see the SMS message with your code
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      value={smsCode}
                      onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="654321"
                      maxLength={6}
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Enter the 6-digit code from your SMS
                    </p>
                  </div>

                  <button
                    onClick={handleVerifyPhone}
                    disabled={smsCode.length !== 6}
                    className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Verify Phone
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 2: Create Password */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                  <Lock size={32} className="text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Create Your Password</h2>
                <p className="text-sm text-gray-600">
                  Create a password for your parent portal access
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handlePasswordChange}
                    className="w-full pl-10 pr-11 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Create a strong password"
                    data-testid="parent-setup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="parent-setup-password-toggle-btn"
                    className="absolute right-3 top-2.5 p-0.5 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {password && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={`h-1 flex-1 rounded ${passwordStrength >= level ? getStrengthColor() : 'bg-gray-200'}`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-600">
                      Password strength: <span className="font-semibold">{getStrengthText()}</span>
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-11 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm your password"
                    data-testid="parent-setup-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(s => !s)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    data-testid="parent-setup-confirm-password-toggle-btn"
                    className="absolute right-3 top-2.5 p-0.5 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleStep2Continue}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Configure Settings - Continue in next file due to length */}
          {step === 3 && (
            <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <Shield size={32} className="text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Configure Parental Controls</h2>
                <p className="text-sm text-gray-600">
                  Customize {parentData.teenName}&apos;s HiBFF experience
                </p>
              </div>

              {/* A. Content Filters */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield size={18} className="text-blue-600" />
                  Content Filters
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.contentFilters.supportAvas}
                      onChange={(e) => setSettings({
                        ...settings,
                        contentFilters: { ...settings.contentFilters, supportAvas: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Support AvAs</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.contentFilters.coachAvas}
                      onChange={(e) => setSettings({
                        ...settings,
                        contentFilters: { ...settings.contentFilters, coachAvas: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Challenging Practice Partners</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.contentFilters.friendAvas}
                      onChange={(e) => setSettings({
                        ...settings,
                        contentFilters: { ...settings.contentFilters, friendAvas: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Friend AvAs</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.contentFilters.memorialAvas}
                      onChange={(e) => setSettings({
                        ...settings,
                        contentFilters: { ...settings.contentFilters, memorialAvas: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Memorial AvAs</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.contentFilters.peerGroups}
                      onChange={(e) => setSettings({
                        ...settings,
                        contentFilters: { ...settings.contentFilters, peerGroups: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Peer Support Groups</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.contentFilters.adultMentorSessions}
                      onChange={(e) => setSettings({
                        ...settings,
                        contentFilters: { ...settings.contentFilters, adultMentorSessions: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Adult Mentor Sessions</span>
                  </label>
                </div>
              </div>

              {/* B. Time Limits */}
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Clock size={18} className="text-purple-600" />
                  Time Limits
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Daily Limit</label>
                    <select
                      value={settings.timeLimits.dailyLimit}
                      onChange={(e) => setSettings({
                        ...settings,
                        timeLimits: { ...settings.timeLimits, dailyLimit: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="30min">30 minutes</option>
                      <option value="1hr">1 hour</option>
                      <option value="2hrs">2 hours</option>
                      <option value="3hrs">3 hours</option>
                      <option value="4hrs">4 hours</option>
                      <option value="unlimited">Unlimited</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quiet Hours Start</label>
                      <input
                        type="time"
                        value={settings.timeLimits.quietHoursStart}
                        onChange={(e) => setSettings({
                          ...settings,
                          timeLimits: { ...settings.timeLimits, quietHoursStart: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quiet Hours End</label>
                      <input
                        type="time"
                        value={settings.timeLimits.quietHoursEnd}
                        onChange={(e) => setSettings({
                          ...settings,
                          timeLimits: { ...settings.timeLimits, quietHoursEnd: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Weekend Limits</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={settings.timeLimits.weekendSame}
                          onChange={() => setSettings({
                            ...settings,
                            timeLimits: { ...settings.timeLimits, weekendSame: true }
                          })}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">Same as weekdays</span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={!settings.timeLimits.weekendSame}
                          onChange={() => setSettings({
                            ...settings,
                            timeLimits: { ...settings.timeLimits, weekendSame: false }
                          })}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">Different</span>
                      </label>
                      {!settings.timeLimits.weekendSame && (
                        <select
                          value={settings.timeLimits.weekendLimit}
                          onChange={(e) => setSettings({
                            ...settings,
                            timeLimits: { ...settings.timeLimits, weekendLimit: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm mt-2"
                        >
                          <option value="30min">30 minutes</option>
                          <option value="1hr">1 hour</option>
                          <option value="2hrs">2 hours</option>
                          <option value="3hrs">3 hours</option>
                          <option value="4hrs">4 hours</option>
                          <option value="unlimited">Unlimited</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* C. Privacy Settings */}
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Eye size={18} className="text-yellow-600" />
                  Privacy Settings - What You Can View
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.privacySettings.viewMoodScores}
                      onChange={(e) => setSettings({
                        ...settings,
                        privacySettings: { ...settings.privacySettings, viewMoodScores: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Daily mood check-in scores</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.privacySettings.viewTopicSummaries}
                      onChange={(e) => setSettings({
                        ...settings,
                        privacySettings: { ...settings.privacySettings, viewTopicSummaries: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Conversation topics (AI summaries, not full transcripts)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.privacySettings.viewTimeSpent}
                      onChange={(e) => setSettings({
                        ...settings,
                        privacySettings: { ...settings.privacySettings, viewTimeSpent: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Time spent on platform per day/week</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.privacySettings.viewGroups}
                      onChange={(e) => setSettings({
                        ...settings,
                        privacySettings: { ...settings.privacySettings, viewGroups: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Groups teen has joined</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.privacySettings.viewFullTranscripts}
                      onChange={(e) => setSettings({
                        ...settings,
                        privacySettings: { ...settings.privacySettings, viewFullTranscripts: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 font-semibold">Full conversation transcripts (not recommended for trust)</span>
                  </label>
                </div>
              </div>

              {/* D. Crisis Alerts */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Bell size={18} className="text-red-600" />
                  Crisis Alerts
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.crisisAlerts.smsCrisisButton}
                      onChange={(e) => setSettings({
                        ...settings,
                        crisisAlerts: { ...settings.crisisAlerts, smsCrisisButton: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Notify me if teen clicks Crisis Help button (SMS)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.crisisAlerts.emailCrisisButton}
                      onChange={(e) => setSettings({
                        ...settings,
                        crisisAlerts: { ...settings.crisisAlerts, emailCrisisButton: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Notify me if teen clicks Crisis Help button (Email)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.crisisAlerts.moodPatternAlert}
                      onChange={(e) => setSettings({
                        ...settings,
                        crisisAlerts: { ...settings.crisisAlerts, moodPatternAlert: e.target.checked }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Notify me if mood scores show concerning pattern (3+ days very low)</span>
                  </label>
                </div>
              </div>

              {/* E. Consent */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={settings.consent}
                    onChange={(e) => setSettings({ ...settings, consent: e.target.checked })}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                  />
                  <span className="text-sm text-gray-700">
                    <strong>I verify that I am the parent/legal guardian of {parentData.teenName}</strong> and consent to monitor their HiBFF usage as described above.
                  </span>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleCompleteSetup}
                  disabled={loading}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Setting up...' : 'Complete Setup & Activate Account'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentSetup;
